import { VisitWorkareaStatus } from "../../common/constants"
import { CalendarEventVisit, ConsultationCalendarEvent, CrewMember, FilterableEvent, TaskCalendarEvent, Timesheet, Visit, VisitWorkareaStatusRecord } from "./scheduleApi"
import { DateRange, EventStatus, EventType, EventTypes, eventHasVisit, getEventStatuses, getVisitForEvent, ScheduleFilters, UNASSIGNED_CREW_ID, UnifiedEventMetadata } from "./scheduleModel"
import { findCrewMemberByEmail } from "./crewModel"

const matchesEventTypeFilter = (eventType: EventType, eventTypes: EventType[]): boolean => {
  if (eventTypes.length === 0) {
    return true
  }
  return eventTypes.includes(eventType)
}

type CrewFilterable = Pick<FilterableEvent, 'crewForemanEmail' | 'crewMemberIds'>

const matchesCrewMemberFilter = (
  event: CrewFilterable,
  crewMemberIds: string[],
  crew: CrewMember[]
): boolean => {
  if (crewMemberIds.length === 0) {
    return true
  }

  const eventCrewIds = event.crewMemberIds
    ? event.crewMemberIds.split(',').map(id => id.trim())
    : []
  const hasMatchingCrew = eventCrewIds.some(id => crewMemberIds.includes(id))

  let hasMatchingForeman = false
  if (event.crewForemanEmail) {
    const foremanCrewMember = findCrewMemberByEmail(crew, event.crewForemanEmail)
    if (foremanCrewMember) {
      hasMatchingForeman = crewMemberIds.includes(foremanCrewMember.id.toString())
    }
  }

  const isUnassignedFilter = crewMemberIds.includes(UNASSIGNED_CREW_ID)
  const hasCrewForemanEmail = event.crewForemanEmail && event.crewForemanEmail.trim() !== ''
  const foremanMatchesCrew = hasCrewForemanEmail && findCrewMemberByEmail(crew, event.crewForemanEmail) !== undefined
  const isUnassignedEvent = !hasCrewForemanEmail || !foremanMatchesCrew

  return hasMatchingCrew || hasMatchingForeman || (isUnassignedFilter && isUnassignedEvent)
}

const matchesDivisionFilter = <T extends FilterableEvent>(
  event: T,
  divisionIds: number[]
): boolean => {
  if (divisionIds.length === 0) {
    return true
  }

  const eventDivisionIds = event.projectAllDivisionIds
    ? event.projectAllDivisionIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
    : []

  return eventDivisionIds.some(id => divisionIds.includes(id))
}

const matchesProjectTypeFilter = <T extends FilterableEvent>(
  event: T,
  projectTypeIds: number[]
): boolean => {
  if (projectTypeIds.length === 0) {
    return true
  }

  return event.projectType !== null && projectTypeIds.includes(event.projectType)
}

const matchesSalesPersonFilter = <T extends FilterableEvent>(
  event: T,
  salesPersonIds: string[]
): boolean => {
  if (salesPersonIds.length === 0) {
    return true
  }

  if (event.salesPersonEmail === null) {
    return false
  }

  const salesPersonEmail = event.salesPersonEmail.toLowerCase()
  return salesPersonIds.some(id => id.toLowerCase() === salesPersonEmail)
}

const matchesEventStatusFilter = <T extends FilterableEvent>(
  event: T,
  eventStatuses: EventStatus[],
  visits: Visit[],
  calendarEventVisits: CalendarEventVisit[],
  timesheets: Timesheet[],
  crew: CrewMember[],
  dateRange: DateRange,
  eventMetadata: Map<number, UnifiedEventMetadata>
): boolean => {
  if (eventStatuses.length === 0) {
    return true
  }

  const isDiscreteVisit = eventMetadata.get(event.id)?.isDiscreteVisit ?? false
  const statuses = getEventStatuses(event, visits, calendarEventVisits, timesheets, crew, dateRange, isDiscreteVisit)
  return statuses.some(s => eventStatuses.includes(s))
}

const matchesMissingVisitsFilter = <T extends FilterableEvent>(
  event: T,
  missingVisits: boolean,
  calendarEventVisits: CalendarEventVisit[],
  visits: Visit[],
  dateRange: DateRange
): boolean => {
  if (!missingVisits) {
    return true
  }

  return !eventHasVisit(event, calendarEventVisits, visits, dateRange)
}

const matchesVisitStatusFilter = <T extends FilterableEvent>(
  event: T,
  visitStatuses: VisitWorkareaStatus[],
  calendarEventVisits: CalendarEventVisit[],
  visits: Visit[],
  visitWorkareaStatuses: VisitWorkareaStatusRecord[],
  dateRange: DateRange
): boolean => {
  if (visitStatuses.length === 0) {
    return true
  }

  const hasVisit = eventHasVisit(event, calendarEventVisits, visits, dateRange)

  if (!hasVisit) {
    return visitStatuses.includes(1)
  }

  const visit = getVisitForEvent(event, calendarEventVisits, visits, dateRange)
  const eventVisitStatuses = visitWorkareaStatuses.filter(vws => vws.visitId === visit?.id)

  return eventVisitStatuses.some(vws => visitStatuses.includes(vws.status))
}

export const filterEvents = <T extends FilterableEvent>(
  events: T[],
  filters: ScheduleFilters,
  visits: Visit[],
  visitWorkareaStatuses: VisitWorkareaStatusRecord[],
  timesheets: Timesheet[],
  crew: CrewMember[],
  calendarEventVisits: CalendarEventVisit[],
  dateRange: DateRange,
  eventMetadata: Map<number, UnifiedEventMetadata>
): T[] => {
  if (!matchesEventTypeFilter(EventTypes.Job, filters.eventTypes)) {
    return []
  }

  return events.filter(event => {
    return (
      matchesCrewMemberFilter(event, filters.crewMemberIds, crew) &&
      matchesDivisionFilter(event, filters.divisionIds) &&
      matchesProjectTypeFilter(event, filters.projectTypeIds) &&
      matchesSalesPersonFilter(event, filters.salesPersonIds) &&
      matchesEventStatusFilter(event, filters.eventStatuses, visits, calendarEventVisits, timesheets, crew, dateRange, eventMetadata) &&
      matchesMissingVisitsFilter(event, filters.missingVisits, calendarEventVisits, visits, dateRange) &&
      matchesVisitStatusFilter(event, filters.visitStatuses, calendarEventVisits, visits, visitWorkareaStatuses, dateRange)
    )
  })
}

const hasJobSpecificFilters = (filters: ScheduleFilters): boolean => {
  const excludesPlannedStatus = filters.eventStatuses.length > 0 && !filters.eventStatuses.includes(EventStatus.Planned)

  return (
    filters.divisionIds.length > 0 ||
    excludesPlannedStatus ||
    filters.missingVisits ||
    filters.visitStatuses.length > 0
  )
}

const matchesConsultationCrewFilter = (
  consultation: ConsultationCalendarEvent,
  crewMemberIds: string[],
  crew: CrewMember[]
): boolean => {
  if (crewMemberIds.length === 0) {
    return true
  }

  const organizerEmail = consultation.organizerEmail

  let hasMatchingOrganizer = false
  if (organizerEmail) {
    const organizerCrewMember = crew.find(
      c => c.userEmail?.toLowerCase() === organizerEmail.toLowerCase()
    )
    if (organizerCrewMember) {
      hasMatchingOrganizer = crewMemberIds.includes(organizerCrewMember.id.toString())
    }
  }

  const isUnassignedFilter = crewMemberIds.includes(UNASSIGNED_CREW_ID)
  const hasOrganizerEmail = !!organizerEmail && organizerEmail.trim() !== ''
  const organizerMatchesCrew = hasOrganizerEmail && crew.some(
    c => c.userEmail?.toLowerCase() === organizerEmail.toLowerCase()
  )
  const isUnassignedConsultation = !hasOrganizerEmail || !organizerMatchesCrew

  return hasMatchingOrganizer || (isUnassignedFilter && isUnassignedConsultation)
}

const matchesConsultationSalesPersonFilter = (
  consultation: ConsultationCalendarEvent,
  salesPersonIds: string[]
): boolean => {
  if (salesPersonIds.length === 0) {
    return true
  }

  const organizerEmail = consultation.organizerEmail.toLowerCase()
  return salesPersonIds.some(id => id.toLowerCase() === organizerEmail)
}

export const filterConsultations = (
  consultations: ConsultationCalendarEvent[],
  filters: ScheduleFilters,
  crew: CrewMember[]
): ConsultationCalendarEvent[] => {
  if (!matchesEventTypeFilter(EventTypes.Consultation, filters.eventTypes)) {
    return []
  }

  if (hasJobSpecificFilters(filters)) {
    return []
  }

  return consultations.filter(consultation =>
    matchesConsultationCrewFilter(consultation, filters.crewMemberIds, crew) &&
    matchesConsultationSalesPersonFilter(consultation, filters.salesPersonIds)
  )
}

export const filterTasks = (
  tasks: TaskCalendarEvent[],
  filters: ScheduleFilters,
  crew: CrewMember[]
): TaskCalendarEvent[] => {
  if (!matchesEventTypeFilter(EventTypes.Task, filters.eventTypes)) {
    return []
  }

  const excludesPlannedStatus = filters.eventStatuses.length > 0 && !filters.eventStatuses.includes(EventStatus.Planned)

  if (filters.divisionIds.length > 0 ||
    filters.salesPersonIds.length > 0 ||
    excludesPlannedStatus ||
    filters.missingVisits ||
    filters.visitStatuses.length > 0) {
    return []
  }

  if (filters.crewMemberIds.length === 0) {
    return tasks
  }

  return tasks.filter(task => matchesCrewMemberFilter(task, filters.crewMemberIds, crew))
}
